import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listOrganizations } from '@/src/api/organization'
import { SimpleCopyCell } from '@/src/components/manage/tables/CopyableCell'
import { useTranslation } from '@/src/i18n/client'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import type { Organization } from '@/src/types/organization.zod'

export const Route = createFileRoute('/_manage/manage/admin/organizations/')({
  component: AdminOrganizationsPage,
})

function AdminOrganizationsPage() {
  const { t } = useTranslation();
  const { data: organizations, isLoading, error } = useQuery({
    queryKey: ['organization', 'list'],
    queryFn: () => listOrganizations(),
  })

  const columns: ColumnConfig<Organization>[] = [
    {
      key: 'id',
      label: t('table.id'),
      width: 'w-24',
      render: (org) => <SimpleCopyCell value={org.id} truncateLength={20} />,
    },
    {
      key: 'name',
      label: t('table.name'),
    },
    {
      key: 'description',
      label: t('table.description'),
      render: (org) => org.description || '-',
    },
    {
      key: 'domain',
      label: t('table.domain'),
      render: (org) => org.domain || '-',
    },
  ];

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <Link to="/manage/admin/organizations/$id" params={{ id: 'new' }}>
            <Button>{t('createOrganization')}</Button>
          </Link>
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable<Organization>
          data={organizations || []}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noOrganizations')}
          actions={(org) => (
            <EditAction
              to="/manage/admin/organizations/$id"
              params={{ id: org.id }}
            />
          )}
        />
      </div>
    </div>
  )
}
